/**
 * POSTLAUNCH-V6 · P2-#11 (Sa 06.09.2026) — Feedback-Prompt-Banner.
 *
 * Erscheint nach ~5 Min aktiver Session. „Später"-Button setzt Session-Flag
 * (nur diese Session unterdrückt), Klick auf „Feedback geben" öffnet
 * Feedback-Modal via Custom-Event.
 */
import React, { useEffect, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { APP_VERSION, APP_BUILD_ID } from "./VersionFooter";

const SESSION_KEY = "f4s_feedback_prompt_dismissed";
const DELAY_MS = 5 * 60 * 1000; // 5 min

export default function FeedbackPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    const t = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  const openFeedback = () => {
    window.dispatchEvent(new CustomEvent("f4s:feedback-open", {
      detail: { version: APP_VERSION, buildId: APP_BUILD_ID, source: "prompt" },
    }));
    dismiss();
  };

  return (
    <div
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 max-w-xs rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg p-4"
      data-testid="feedback-prompt"
      role="dialog"
      aria-labelledby="feedback-prompt-title"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="feedback-prompt-dismiss"
        aria-label="Später"
        className="absolute top-2 right-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-start gap-2.5">
        <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[var(--fts-primary-bright)]" aria-hidden="true" />
        <div className="min-w-0 pr-4">
          <p
            id="feedback-prompt-title"
            className="text-sm font-bold text-slate-900 dark:text-white"
          >
            Wie läuft's mit v{APP_VERSION}?
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300 leading-snug">
            Zwei Sätze reichen — dein Eindruck hilft uns die nächste Version besser zu machen.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={openFeedback}
              data-testid="feedback-prompt-open-btn"
              className="rounded-lg bg-[var(--fts-primary-bright)] hover:opacity-90 text-white text-xs font-bold px-3 py-1.5"
            >
              Feedback geben
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="feedback-prompt-later-btn"
              className="text-xs font-semibold text-slate-500 dark:text-slate-400 hover:underline"
            >
              Später
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
